import type { ChatStatus } from './use-chat-status';
import type { Approval } from './use-approvals';

/**
 * FRONT-2343: does the cached approvals list (`approvalKeys.list()`) disagree
 * with what the chat-status poll just read straight from Postgres?
 *
 * Eddy runs 2 backend replicas and the in-process event bus has no
 * cross-replica relay, so a pushed `approval-request`/`approval-decided`
 * event can be published on the replica this browser's SSE connection isn't
 * on, and never reach the approvals cache. The chat-status poll (already
 * hitting the DB every 2s while any chat is working/needs-you) carries each
 * session's pending-approval ids
 * (`ChatStatus.pendingApprovalIds`, src/agents/chat-status-store.ts) as a
 * cross-replica-safe fallback — this function is the diff that decides
 * whether the cache needs a refetch.
 *
 * True in EITHER direction:
 *  - the server lists an id the cache doesn't hold (a request push that
 *    never arrived — the missing "Quick OK?" card), or
 *  - the cache still holds an undecided row for a session the server no
 *    longer lists an id for (a decision push that never arrived — a card
 *    left lingering after it was actually resolved elsewhere).
 *
 * A cached approval whose `originExternalId` isn't one of the sessions in
 * `statuses` is ignored — this function only judges sessions the caller
 * actually asked chat-status about.
 */
export function approvalsCacheDisagrees(
  statuses: Record<string, ChatStatus>,
  cached: Approval[] | undefined,
): boolean {
  for (const sessionId of Object.keys(statuses)) {
    const serverIds = statuses[sessionId].pendingApprovalIds ?? [];

    if (cached === undefined) {
      // The approvals list hasn't loaded at all yet. Only worth a refetch if
      // the server actually has something for this session to show.
      if (serverIds.length > 0) return true;
      continue;
    }

    const cacheIds = cached
      .filter(
        (a) =>
          a.originExternalId === sessionId &&
          a.status !== 'approved' &&
          a.status !== 'rejected',
      )
      .map((a) => a.id);

    if (serverIds.length !== cacheIds.length) return true;

    const cacheSet = new Set(cacheIds);
    for (const id of serverIds) {
      if (!cacheSet.has(id)) return true;
    }
    const serverSet = new Set(serverIds);
    for (const id of cacheIds) {
      if (!serverSet.has(id)) return true;
    }
  }
  return false;
}
